'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { sleep } from '@/lib/utils';

interface Props {
  handleConnection?: (port: ISerialPort) => void;
  handleDisconnection?: (port: ISerialPort) => void;
}

type Reader = ReadableStreamDefaultReader<Uint8Array>;

export const useSerialPort = (props: Props) => {
  const { handleDisconnection, handleConnection } = props;
  const [ports, setPorts] = useState<ISerialPort[]>([]);
  const readerMapRef = useRef<WeakMap<ISerialPort, Reader>>(new WeakMap());

  // Fila de operações por porta. open()/close() fazem check-then-act sobre um
  // recurso compartilhado (a porta), então dois fluxos concorrentes — p.ex. o
  // ciclo de detecção e um auto teste — podiam intercalar e disparar
  // "open() already in progress" ou leituras vazias. Portas distintas seguem
  // em paralelo; apenas as operações de uma mesma porta são serializadas.
  const portLocksRef = useRef<WeakMap<ISerialPort, Promise<unknown>>>(new WeakMap());

  const withPortLock = useCallback(<T>(port: ISerialPort, op: () => Promise<T>): Promise<T> => {
    const previous = portLocksRef.current.get(port) ?? Promise.resolve();
    // Encadeia após a operação anterior, ignorando o erro dela para não
    // envenenar a fila — cada operação reporta o próprio erro ao seu chamador.
    const next = previous.then(op, op);
    portLocksRef.current.set(
      port,
      next.catch(() => undefined),
    );
    return next;
  }, []);

  const releaseReader = useCallback(async (port: ISerialPort) => {
    const reader = readerMapRef.current.get(port);
    if (!reader) {
      return;
    }

    readerMapRef.current.delete(port);

    try {
      await reader.cancel();
    } catch (error) {
      console.error('Error cancelling reader:', error);
    } finally {
      try {
        reader.releaseLock();
      } catch (releaseError) {
        console.error('Error releasing reader lock:', releaseError);
      }
    }
  }, []);

  const handleConnect = useCallback(
    (e: Event) => {
      const target = e.target as ISerialPort | null;
      if (!target) return;
      // Nunca duplica: a mesma SerialPort pode chegar de novo via reset de USB do device.
      // Duplicatas fazem a detecção abrir a mesma porta N vezes em paralelo → NetworkError.
      setPorts((prev) => (prev.includes(target) ? prev : [...prev, target]));
      handleConnection?.(target);
    },
    [handleConnection],
  );

  const handleDisconnect = useCallback(
    (e: Event) => {
      const target = e.target as ISerialPort | null;
      if (!target) return;
      setPorts((prev) => prev.filter((el) => el !== target));
      void releaseReader(target);
      handleDisconnection?.(target);
    },
    [handleDisconnection, releaseReader],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && 'serial' in navigator) {
      const _navigator = navigator as INavigator;

      _navigator.serial.getPorts().then((ports) => {
        setPorts(ports);
      });

      _navigator.serial.addEventListener('connect', handleConnect);
      _navigator.serial.addEventListener('disconnect', handleDisconnect);

      return () => {
        _navigator.serial.removeEventListener('connect', handleConnect);
        _navigator.serial.removeEventListener('disconnect', handleDisconnect);
      };
    }
  }, [handleConnect, handleDisconnect]);

  //Serial
  const requestPort = async (): Promise<ISerialPort | undefined> => {
    try {
      const port = await (navigator as INavigator).serial.requestPort();
      // requestPort devolve a MESMA instância se o usuário escolher um device já autorizado
      // (ex.: botão "Nova Porta" num device já listado) — não duplica na lista.
      setPorts((prev) => (prev.includes(port) ? prev : [...prev, port]));
      return port;
    } catch (err) {
      console.error('Error requesting port:', err);
      return undefined;
    }
  };

  //SerialPort
  const openPort = async (port: ISerialPort, options: SerialOptions, settleDelay = 350) =>
    withPortLock(port, async () => {
      try {
        // Check if port is already open by attempting to get signals
        // If port is open, getSignals will succeed; if closed, it will throw
        try {
          await port.getSignals();
          // Port is already open, no need to reopen
          return;
        } catch {
          // Port is closed, proceed to open
        }

        await port.open(options);
        // Espera o conversor USB-serial estabilizar após o open (bytes enviados cedo demais
        // são descartados por alguns adaptadores). Ajustável por tecnologia via settleDelay.
        await sleep(settleDelay);
      } catch (e) {
        console.error('on open port', e);
        throw e;
      }
    });

  const closePort = async (port: ISerialPort) =>
    withPortLock(port, async () => {
      try {
        // Try to get signals to check if port is actually open
        try {
          await port.getSignals();
        } catch {
          // Port is already closed, nothing to do
          return;
        }

        if (port.readable) {
          if (port.readable.locked) {
            await releaseReader(port);
          }
        }

        if (port.writable) {
          if (port.writable.locked) {
            try {
              const writer = port.writable.getWriter();
              await writer.abort();
              writer.releaseLock();
            } catch {
              // Writer might be locked by another operation
            }
          }
        }

        await port.close();
      } catch (error) {
        // If port is already closed or closing, that's fine
        if (error instanceof Error && error.message.includes('already')) {
          return;
        }
        throw error instanceof Error ? error : new Error('Error when close port');
      }
    });

  const forgetPort = async (port: ISerialPort): Promise<void> => {
    try {
      await releaseReader(port);
      await port.forget();
    } catch (error) {
      throw error instanceof Error ? error : new Error('Error when forgetting serial port');
    }
  };

  const getInfo = (port: ISerialPort) => {
    const info = port.getInfo();
    return info;
  };

  const getSignals = async (port: ISerialPort) => {
    const signals = await port.getSignals();
    return signals;
  };

  const getReader = async (port: ISerialPort) => {
    if (!port?.readable) {
      throw Error('Readable stream not available');
    }

    const storedReader = readerMapRef.current.get(port);
    if (storedReader) {
      return storedReader;
    }

    if (port.readable.locked) {
      throw Error('Readable stream is already locked');
    }

    const reader = port.readable.getReader();

    const cleanup = () => {
      readerMapRef.current.delete(port);
    };

    reader.closed.catch(() => undefined).finally(cleanup);

    const originalReleaseLock = reader.releaseLock.bind(reader);
    reader.releaseLock = () => {
      cleanup();
      originalReleaseLock();
    };

    readerMapRef.current.set(port, reader);

    return reader;
  };

  const writeToPort = async (port: ISerialPort, data: string) => {
    if (!port.writable) {
      throw new Error('Writable stream not available');
    }

    try {
      const writer = port.writable.getWriter();
      if (!writer) {
        throw new Error('Writer not available');
      }
      const encoder = new TextEncoder();
      await writer.write(encoder.encode(data));
      writer.releaseLock();
    } catch (error) {
      throw error instanceof Error ? error : new Error('Error writing to port');
    }
  };

  // Check if Web Serial API is supported
  const isSupported = typeof window !== 'undefined' && 'serial' in navigator;

  return {
    ports,
    requestPort,
    closePort,
    forgetPort,
    getInfo,
    getSignals,
    openPort,
    writeToPort,
    getReader,
    releaseReader,
    isSupported,
  };
};

export interface ISerialPort {
  readonly readable: ReadableStream<Uint8Array> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  open(options: SerialOptions): Promise<void>;
  close(): Promise<void>;
  forget(): Promise<void>;
  getInfo(): SerialPortInfo;
  setSignals(signals?: SerialOutputSignals): Promise<void>;
  getSignals(): Promise<SerialInputSignals>;
  addEventListener(
    type: 'connect' | 'disconnect',
    listener: (this: ISerialPort, ev: Event) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener(
    type: 'connect' | 'disconnect',
    listener: (this: ISerialPort, ev: Event) => any,
    options?: boolean | EventListenerOptions,
  ): void;
}

export interface SerialOptions {
  baudRate: number;
  dataBits?: 7 | 8;
  stopBits?: 1 | 2;
  parity?: 'none' | 'even' | 'odd';
  bufferSize?: number;
  flowControl?: 'none' | 'hardware';
}

export interface SerialPortInfo {
  usbVendorId?: number;
  usbProductId?: number;
}

export interface SerialOutputSignals {
  dataTerminalReady?: boolean;
  requestToSend?: boolean;
  break?: boolean;
}

export interface SerialInputSignals {
  dataCarrierDetect: boolean;
  clearToSend: boolean;
  ringIndicator: boolean;
  dataSetReady: boolean;
}

//=========================================

interface SerialPortRequestOptions {
  filters?: SerialPortFilter[];
}

interface SerialPortFilter {
  usbVendorId?: number;
  usbProductId?: number;
}

interface Serial {
  getPorts(): Promise<ISerialPort[]>;
  requestPort(options?: SerialPortRequestOptions): Promise<ISerialPort>;
  addEventListener(
    type: 'connect' | 'disconnect',
    listener: (this: Navigator, ev: Event) => any,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener(
    type: 'connect' | 'disconnect',
    listener: (this: Navigator, ev: Event) => any,
    options?: boolean | EventListenerOptions,
  ): void;
}

export interface INavigator extends Navigator {
  serial: Serial;
}

export const readReader = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeout = 10_000, // timeout total (hard-stop)
  idleMs = 1500, // encerra se ficar ocioso por esse tempo
  expectedMarkers?: string[], // se fornecido, encerra cedo quando TODOS aparecerem no buffer
): Promise<string[]> => {
  const decoder = new TextDecoder();
  const lines = new Set<string>();
  let buffer = '';
  const earlyExitMarkers = expectedMarkers?.filter((m) => m.length > 0) ?? [];
  const pendingMarkers = new Set(earlyExitMarkers);

  const start = Date.now();
  let idleBudget = idleMs; // janela de ociosidade (pode ser ajustada se quiser)

  // Lê um passo: corrida entre read() e timer de ociosidade
  const readOnceOrIdle = async (): Promise<'read' | 'idle' | 'done'> => {
    const idleSentinel = Symbol('idle');
    const perRead = reader.read();

    const winner = await Promise.race([
      perRead,
      new Promise<typeof idleSentinel>((resolve) =>
        setTimeout(() => resolve(idleSentinel), idleBudget),
      ),
    ]);

    if (winner === idleSentinel) return 'idle';

    const { value, done } = winner as ReadableStreamReadResult<Uint8Array>;
    if (done) return 'done';

    if (value?.byteLength) {
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split(/\r?\n/);
      buffer = parts.pop() ?? '';
      for (const line of parts) {
        if (line.length) {
          lines.add(line);
          if (pendingMarkers.size > 0) {
            for (const marker of pendingMarkers) {
              if (line.includes(marker)) pendingMarkers.delete(marker);
            }
          }
        }
      }

      // recebeu dados → reseta janela de ociosidade
      idleBudget = idleMs;
      return 'read';
    }

    return 'read';
  };

  try {
    while (true) {
      // Hard timeout total
      if (Date.now() - start > timeout) break;

      // Early exit: todos os marcadores esperados já chegaram
      if (earlyExitMarkers.length > 0 && pendingMarkers.size === 0) break;

      const state = await readOnceOrIdle();

      if (state === 'done') break;

      if (state === 'idle') {
        break;
      }

      // state === "read": continua
    }
  } finally {
    // empurra eventual parcial
    if (buffer.length) lines.add(buffer);

    // Se estourou o timeout total e o read() ficou pendente, cancele para liberar o lock
    if (Date.now() - start > timeout) {
      try {
        await reader.cancel();
      } catch {}
      try {
        reader.releaseLock();
      } catch {}
    }
  }

  return Array.from(lines);
};

// Lê o reader continuamente em segundo plano, acumulando as linhas recebidas.
// O loop termina sozinho quando a porta é fechada (o reader é cancelado).
export const collectLines = (reader: ReadableStreamDefaultReader<Uint8Array>) => {
  const decoder = new TextDecoder();
  const lines: string[] = [];
  let buffer = '';
  let lastDataAt = Date.now();

  void (async () => {
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value?.byteLength) continue;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\r?\n/);
        buffer = parts.pop() ?? '';
        for (const part of parts) {
          if (part.length) lines.push(part);
        }
        lastDataAt = Date.now();
      }
    } catch {
      // Porta fechada/desconectada durante a leitura — fica com o que já chegou.
    }
  })();

  return {
    snapshot: () => (buffer.length ? [...lines, buffer] : [...lines]),
    lastDataAt: () => lastDataAt,
  };
};

export default useSerialPort;
